import { useState } from "react";
import { useMutation, useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom";
import { useAppDispatch } from "../../hooks/hooks";
import { LessonPlanDocumentType, LessonPlanType } from "../../types/LessonPlan";
import { useGetLessonPlansQuery } from "../../hooks/useGetLessonPlansQuery";
import { updateLessonPlan } from "../../services/school/lessonPlans/updateLessonPlan";
import { Key_lessonPlans } from "../../utils/constants";
import toast from "react-hot-toast";
import { routes } from "../../routes";
import { ArchiveIcon, ChevronLeftIcon, EnterIcon, Pencil1Icon, TrashIcon } from "@radix-ui/react-icons";
import { AlertDialog, Flex, Button, Card, Spinner,Text, Box, Callout } from "@radix-ui/themes";
import { Lists } from "../../components/common/Lists";
import TableComponent from "../../components/common/Tables";
import { useSelector } from "react-redux";
import { RootState } from "../../redux/store";
import { createLessonPlan, createLessonPlanDocument } from "../../services/school/lessonPlans/createLessonPlan";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { CreateLessonPlanFormValidation } from "./CreateLessonPlanFormValidation";
import TextAreaFieldComponent from "../../components/inputs/TextAreafield/TextAreafieldComponent";
import DateFieldComponent from "../../components/inputs/Datefield/DatefieldComponent";
import { DragAndDropFileUpload } from "../../components/inputs/FileUpload/DragAndDropFileUpload";
import { dataURLtoFile, getFile } from "../../utils/fileUtils";
import { formatDate, formatDateForInput, formatDateForOutput } from "../../utils/dateFormatter";
import { setLessonPlanData } from "../../redux/slices/school/lessonPlan";
import { getLessonPlansDocuments } from "../../services/school/lessonPlans/getLessonPlans";

const columnNamesMap: { [key: string]: string } = {
  subject:"Disciplina", 
  class:"Turma", 
  dateDisplay:"Data", 
  objectives:"Objectivos", 
  resources:"Recursos", 
  activities:"Actividades", 
};

export const ManageLessonPlans = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const subjectPlanData = useSelector((state: RootState) => state.subjectPlan);
  const mutation = useMutation({ mutationFn: createLessonPlan, mutationKey: 'createLessonPlan' });
  const mutationCreateLessonPlanDocument = useMutation({ mutationFn: createLessonPlanDocument, mutationKey: 'createLessonPlanDocument' });

  const queryClient = useQueryClient();
  const [itemToBeDeleted, setItemToBeDeleted] = useState<LessonPlanType | null>(null);
  const { data, status, refetch } = useGetLessonPlansQuery({
    PageNumber: currentPage, PageSize: 400,
    IdSubjectPlan: subjectPlanData.id,
    IdCourse: subjectPlanData.idCourse,
    IdTeacher: subjectPlanData.idTeacher,
    IdClass: subjectPlanData.idClass,
    IdSubject: subjectPlanData.idSubject
  });
  const mutationDelete = useMutation(updateLessonPlan, {
    onSuccess: () => {
      queryClient.invalidateQueries(Key_lessonPlans);
      toast.success('Apagado');
    },
    onError: () => {
      toast.error("Erro ao apagar.")
    }
  });
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const openDeleteDialog = (s: LessonPlanType) => {
    setItemToBeDeleted(s);
  };
  const confirmDelete = () => {
    if (itemToBeDeleted) {
      const updatedSubscription = { ...itemToBeDeleted, status: false };
      mutationDelete.mutateAsync(updatedSubscription);
      setItemToBeDeleted(null);
    }
  };

  const handleClickEdit = async (lessonPlan: LessonPlanType) => {
    try {
      const {items} = await getLessonPlansDocuments({PageNumber:1, PageSize:100, IdLessonPlan: lessonPlan.id})
      if(items.length>0){
        const documents = items?.map((document: LessonPlanDocumentType) => {
         // const file = dataURLtoFile(document.docBase64, document.filename);
          return {
              ...document,
           //   file:file,
         //     docBase64:''
          };
      });
      lessonPlan.documents = documents
    }
      lessonPlan.description = lessonPlan.description??"";
      dispatch(setLessonPlanData(lessonPlan))
      navigate(routes.updateLessonPlan)
      
    } catch (error) {
      toast.error("Não foi possivel realizar o seu pedido")
    } 
  };
  const handleClickBack = async () => {
    navigate(routes.getSubjectPlans)
  };
  const options = data?.items.map(() => [
    {
      label: 'Editar',
      onClick: (s: LessonPlanType) => handleClickEdit(s),
      icon: <Pencil1Icon />
    },
    {
      label: 'Excluir',
      onClick: (s: LessonPlanType) => openDeleteDialog(s),
      icon: <TrashIcon />
    },
    // {
    //   label: 'Imprimir',
    //   onClick: (s: LessonPlanType) => handlePrint(s),
    //   icon: <ArchiveIcon />
    // },

  ]) || [];
  const defaultValues:LessonPlanType ={
    status: true, 
    idTeacher: subjectPlanData.idTeacher, 
    idClass: subjectPlanData.idClass, 
    idSubject: subjectPlanData.idSubject, 
    idSubjectPlan: subjectPlanData.id, 
    date: new Date().toISOString().split('T')[0],
    documents:[]
  }
  const { control, handleSubmit, setValue, getValues, formState: { errors }, watch, trigger, reset } = useForm<LessonPlanType>({
    resolver: yupResolver(CreateLessonPlanFormValidation),
    defaultValues,
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    shouldUseNativeValidation: false,
  });
  const onSubmit = handleSubmit(async (data) => {
    
    try {
      
      const result = await mutation.mutateAsync(data);
      if (result) {
        if(data.documents?.length===0){
          toast.success("Criado!");
          reset();
          refetch();
          return;
        }
        if(data.documents && data.documents?.length>0){
          const convertedToBase64Docs = await Promise.all(data.documents?.map(async (document, index) => {
            const file = document.file as File
            return getFile(file, file.name)
          }));
          await Promise.all(convertedToBase64Docs.map((cv) => {
            return mutationCreateLessonPlanDocument.mutateAsync({
              docBase64: cv.base64,
              filename: cv.name,
              status: true,
              idLessonPlan: result,
              description: "",

            })
          }))
        }
        toast.success("Criado!");
        reset();
        refetch();
      }
    } catch (err: any) {
      toast.error(`${mutation.error ?? err?.message}`);
    }
  });
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'documents',
  });

  {
    if (!subjectPlanData || !subjectPlanData.id) {
      return (
        <Flex gap='3'>
          <Text as="label" color="red" onClick={()=> handleClickBack()}>Sem plano selecionado, Clique aqui </Text>
          
        </Flex>
      );

    }
  }
  
  return (
    <>
      <Card>
        <Flex direction="row" gap='6'>
          <Flex direction="column" gap="5" style={{ flex: 1 }}>
            <form onSubmit={onSubmit} noValidate>
              <Flex direction='column' gap='4'>
              <TextAreaFieldComponent name="objectives" label="Objectivos" control={control} errors={errors} />
              <TextAreaFieldComponent name="activities" label="Atividades" control={control} errors={errors} />
              <TextAreaFieldComponent name="resources" label="Recursos" control={control} errors={errors} />
              <DateFieldComponent
                name="date"
                label="Data"
                control={control}
                errors={errors}
              />
                <Flex direction="column" gap="3">
                  <Text as="label">Documentos</Text>
                  {fields.map((f, index) => (
                    <Box
                      key={index}
                      style={{
                        padding: '10px',
                        border: '1px solid #ddd',
                        borderRadius: '5px',
                        marginBottom: '10px',
                        position: 'relative'
                      }}
                    >
                      <Controller
                        name={`documents.${index}.file` as const}
                        control={control}
                        render={({ field }) => (
                          <DragAndDropFileUpload
                            onFileDrop={(file) => {
                              field.onChange(file);
                            }}
                            accept="*"
                            selectedFile={field.value}
                            remove={() => {
                              remove(index);
                            }}
                          />
                        )}
                      />
                      {errors.documents?.[index]?.file && (
                        <Text color="red" size="1" style={{ marginTop: '5px' }}>
                          {`${errors.documents[index]?.file?.message}`}
                        </Text>
                      )}
                      <Text
                        color="red"
                        size="1"
                        style={{
                          cursor: 'pointer',
                          position: 'absolute',
                          top: '10px',
                          right: '10px',
                          fontWeight: 'bold'
                        }}
                        onClick={() => {
                          remove(index);
                        }}
                      >
                        Remover
                      </Text>
                    </Box>
                  ))}
                  <Button
                    type="button"
                    variant="surface"
                    color="blue"
                    onClick={() => append({ file: null })}
                  >
                    Adicionar Documento
                  </Button>
                </Flex>

                <Flex direction='row' gap='4' style={{ alignSelf: 'end' }}>
                  <Button color='red' onClick={handleClickBack} disabled={mutation.isLoading}>
                    <ChevronLeftIcon /> voltar
                  </Button>
                  <Button type="submit" disabled={mutation.isLoading} variant='surface'>
                    {mutation.isLoading ? <Spinner loading> <EnterIcon /></Spinner> : <EnterIcon />}
                    Registar
                  </Button>
                </Flex>
              </Flex>
            </form>
          </Flex>
          <Flex direction="column" gap="4" style={{ flex: 1 }}>
            <>
              <Lists
                status={status}
                TableComponent={() =>
                  <TableComponent
                    columns={Object.keys(columnNamesMap)}
                    items={data?.items.map(i=>{
                      return {...i, dateDisplay:formatDateForInput(i.date!)}
                    })}
                    options={options}
                    columnNamesMap={columnNamesMap}
                    totalPages={data?.totalPages || 1}
                    currentPage={currentPage}
                    onPageChange={handlePageChange}
                  />
                }
              //  handleClickCreate={handleClickCreate}
              // handleClickBack={handleClickBack}
              />
              {itemToBeDeleted && (
                <AlertDialog.Root open={!!itemToBeDeleted} onOpenChange={() => setItemToBeDeleted(null)} >
                  <AlertDialog.Content maxWidth="450px" >
                    <AlertDialog.Title>(Apagar) Esta operação é irreversível </AlertDialog.Title>
                    <Flex gap="3" mt="4" justify="end">
                      <AlertDialog.Cancel >
                        <Button variant="soft" color="gray">Cancelar</Button>
                      </AlertDialog.Cancel>
                      <AlertDialog.Action >
                        <Button variant="solid" color="red" onClick={confirmDelete}>
                          Continuar
                        </Button>
                      </AlertDialog.Action>
                    </Flex>
                  </AlertDialog.Content>
                </AlertDialog.Root>
              )}
            </>
          </Flex>
        </Flex>
      </Card>


    </>
  );
}
